import { map, size, some, uniqBy } from "lodash-es";
import React, { useEffect, useMemo } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { createFilter } from "react-select";

import { FilterOptionOption } from "react-select/dist/declarations/src/filters";
import { CaretDown } from "shared/assets/icons";
import { InputFieldProps, DefaultOption as Option } from "shared/components";
import { FieldWrapper } from "../FieldWrapper";
import { multiSelectConfig } from "./config";
import {
  MultiSelectContainer,
  StyledDropdownIndicator,
  StyledMultiSelect,
} from "./styles";
import { ISelectCategoryOption, ISelectOption } from "./types";

interface MultiSelectProps extends Omit<InputFieldProps, "value"> {
  value?: ISelectOption[] | null;
  defaultValue?: string[];
  categories?: ISelectCategoryOption[];
  options: ISelectOption[];
  control: Control;
  validation?: RegisterOptions;
  setFieldValue?: (field: string, value: string[] | null) => void;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  name,
  value,
  categories = [],
  options,
  placeholder,
  control,
  defaultValue,
  onChange: handleChange,
  validation,
  setFieldValue,
  error,
  disabled,
  tabIndex,
  ...props
}) => {
  const visibleCategories = useMemo(
    () =>
      categories.filter(({ items }) =>
        some(items, (item) => !map(value, "value")?.includes(item.value))
      ),
    [categories, value]
  );

  const visibleOptions = useMemo(
    () => [...visibleCategories, ...options],
    [options, visibleCategories]
  );

  const filterConfig = {
    stringify: ({ label, value, data }: FilterOptionOption<any>) =>
      `${label} ${value} ${data.searchableLabel}`,
  };

  const defaultValues = useMemo((): ISelectOption[] | undefined => {
    if (!defaultValue) return undefined;

    const valueAsOptions = defaultValue?.map((val) =>
      options.find((option) => val === option.value)
    );

    return valueAsOptions.filter(
      (option) => option !== undefined
    ) as ISelectOption[];
  }, [defaultValue, options]);

  // Manually reset value after a react-hook-form reset()
  // For controlled components like React-Select which do not expose a ref prop,
  // you will have to reset the input value manually with setValue
  // https://react-hook-form.com/api/useform/reset
  useEffect(() => {
    if (value === undefined) {
      setFieldValue && setFieldValue(name, null);
      handleChange && handleChange(undefined);
    }
  }, [name, value, setFieldValue, handleChange]);

  return (
    <FieldWrapper name={name} error={error} {...props}>
      <MultiSelectContainer>
        <Controller
          name={name}
          control={control}
          rules={validation}
          defaultValue={defaultValues}
          render={({ onChange }) => (
            <StyledMultiSelect
              {...multiSelectConfig(!!error)}
              id={name}
              value={value || null}
              isDisabled={disabled}
              tabIndex={tabIndex}
              onChange={(selectedValues: any) => {
                const selectedOptions = uniqBy(
                  (selectedValues as ISelectOption[]).reduce(
                    (
                      selectedOptions: ISelectOption[],
                      option: ISelectOption
                    ) => {
                      if (option.type === "category") {
                        const newOptions = (option as ISelectCategoryOption)
                          .items;
                        return [...selectedOptions, ...newOptions];
                      } else {
                        return [...selectedOptions, option];
                      }
                    },
                    []
                  ),
                  "value"
                );

                onChange(selectedOptions.length ? selectedOptions : undefined);

                const filtered = selectedOptions.filter(
                  (option: ISelectOption) => option.type !== "category"
                );

                handleChange &&
                  handleChange(size(filtered) ? filtered : undefined);
              }}
              options={visibleOptions}
              placeholder={placeholder}
              isClearable
              components={{
                IndicatorSeparator: null,
                DropdownIndicator: ({ isFocused }: { isFocused: boolean }) => (
                  <StyledDropdownIndicator isOpen={isFocused}>
                    <CaretDown />
                  </StyledDropdownIndicator>
                ),
                Option,
              }}
              filterOption={createFilter(filterConfig)}
              styles={{
                option: (styles: any, { data }: { data: any }) => ({
                  ...styles,
                  fontWeight: data.type === "category" && "bold",
                }),
              }}
            />
          )}
        />
      </MultiSelectContainer>
    </FieldWrapper>
  );
};
